// t603 · B-2443 · PRIMERO (antes de que otro módulo lea el almacenamiento del navegador o pida algo): la página de una clínica con la
// edición local por el puente (/l/<instalación>/...). En cualquier otra dirección no hace nada (utils/puenteLocal.js).
import { puente as puenteLocal } from './utils/puenteLocalAntes'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import './index.css'
import { aplicarZoom, getZoom, vigilarCortes } from './utils/uiScale'
import { RegionalProvider } from './context/RegionalContext'
import { suscribirVisita } from './services/api'
import { alLimpiarSesion } from './services/sesion'
import { evitarZoomAlEscribirEnIos } from './utils/zoomIos'
import { vigilarVersionNueva } from './components/ErrorBoundary'
import { pintarFranjaDeLaCompilacion } from './utils/franjaEntorno'
import { ponerManifiestoDelLaboratorio, ponerManifiestoDelPaciente } from './utils/salidaDelPortal'
import { instalarDescargasEnLaApp } from './utils/descargasEnLaApp'
// t523 · B-2259 · el idioma que recuerda este dispositivo (utils/i18nCargar): ver abajo, antes de pintar. Y las pantallas
// del paciente (pre-registro, consentimiento, presupuesto, pago...), en el suyo: lo dice el servidor en cada respuesta
// (utils/i18nPublico); con la función «idiomas» apagada no llega ninguna cabecera y no cambia nada.
import axios from 'axios'
import { prepararIdiomaGuardado } from './utils/i18nCargar'
import { instalarIdiomaPublico, esPantallaDelPaciente } from './utils/i18nPublico'

// #7: aplicar el tamaño de interfaz guardado antes de pintar (evita parpadeo).
aplicarZoom(getZoom())
// t264c · en el portal del laboratorio (/lab), el manifiesto de SU app («Biango Laboratorio», start_url /lab/): quien lo
// instala desde ahí abre siempre su portal, nunca el login de las clínicas (utils/salidaDelPortal).
ponerManifiestoDelLaboratorio(document, window.location.pathname)
// B-2270 · y en el portal del paciente (/portal), el de SU app instalable («Mi portal», start_url /portal): «Añadir a la
// pantalla de inicio» desde el portal abre su portal, no el login del equipo (utils/salidaDelPortal, utils/accesoPortal).
ponerManifiestoDelPaciente(document, window.location.pathname)
// B-868 · los cortes de móvil/tablet de las hojas de estilo, con el ANCHO EFECTIVO, y al redimensionar (utils/uiScale).
vigilarCortes()
// B-838 · en el iPhone, tocar un campo acercaba la pantalla y en la app no había forma de alejarla (utils/zoomIos).
evitarZoomAlEscribirEnIos()
// B-867 · si tras una subida falta un trozo de la versión anterior, la página se recarga sola una vez (components/ErrorBoundary).
vigilarVersionNueva()
// ENTORNO DE PRUEBAS · el marco naranja en todas las páginas, solo si se compiló con VITE_ENTORNO=pruebas (lo pone
// routes/deploy.js en el servidor de pruebas). En producción no hace nada (utils/franjaEntorno).
pintarFranjaDeLaCompilacion()
// 17-oct-2026 · en la app del móvil, un <a download> no hace nada: cualquier archivo que se baje así se abre en el visor de la
// app, que lo enseña o lo deja descargar por el navegador del teléfono. En un navegador no se instala (utils/descargasEnLaApp).
instalarDescargasEnLaApp()

const queryClient = new QueryClient()

// ── LA CACHE DE CONSULTAS NO PUEDE CRUZAR DE UNA CLINICA A OTRA ──────────────────────────────────
//
//  ES UN SOLO `QueryClient` PARA TODA LA VIDA DE LA PESTAÑA, y entrar en una clinica de un cliente
//  (o salir de ella) NO recarga la pagina: es un `navigate()`. Sin esto, todo lo que react-query
//  tuviera guardado seguia sirviendose despues del cambio, con la clinica ya cambiada debajo.
//
//  MEDIDO, y por donde mas duele: la vista previa de un presupuesto y de una factura guardaba la
//  marca bajo la clave `['branding']` —sin la clinica— con cinco minutos de frescura. Cebada en la
//  casa propia y entrando en la clinica 7, el documento del CLIENTE salia con el nombre, el logo y
//  el color del soporte sin ir al servidor; al reves, la marca del cliente encima de un presupuesto
//  propio. Y no era solo la marca: los "Datos de la clinica" del asistente (`['datos-bot']`) tenian el
//  mismo agujero.
//
//  Se arregla EN LA RAIZ y no clave por clave: cambiar de clinica vacia la cache entera. Un hueco
//  vacio durante un instante es un estado normal de la aplicacion; el nombre de otra empresa encima
//  de los datos de un paciente, no.
//  Y lo mismo al limpiar la sesion: cerrar sesion tampoco recarga, asi que sin esto la siguiente
//  persona que entrara en ese ordenador empezaria viendo lo que habia cargado la anterior.
//
//  PERO `clear()` NO BASTA, Y AQUI DECIA QUE SI. Este bloque prometia que lo que siguiera montado
//  se enteraba SOLO, ya con la cabecera de la clinica nueva, y era FALSO. MEDIDO con el
//  `QueryObserver` de verdad: `clear()` vacia la cache, pero un observador YA MONTADO CONSERVA su
//  ultimo resultado y no vuelve a pedir nada hasta que React lo repinta. O sea que la vista previa
//  EN VIVO de un presupuesto y de una factura (`Pacientes.jsx`, DocPreview) y las dos tarjetas de
//  «Datos de la clinica» de `Ajustes.jsx` seguian pintando el membrete de la clinica ANTERIOR
//  despues del cambio — que es exactamente el documento falso que este bloque dice impedir.
//  Y el cambio de visita NO siempre viene de un `navigate()`: llega tambien cuando la clinica CORTA
//  el acceso (403 -> `terminarVisita`) y desde OTRA PESTAÑA (evento `storage`), donde no hay
//  ninguna navegacion que repinte nada.
//
//  LA OTRA MITAD VIVE EN LA PUERTA DE LA IDENTIDAD: `hooks/useBranding` avisa a las pantallas que ya
//  estan montadas (`_mirando` / `_version`) y `useMarcaClinica` se suscribe a ese aviso. Al llegar,
//  esas pantallas vuelven a pintar, su clave de cache ya lleva la clinica nueva y react-query si va
//  al servidor. Este `clear()` sigue haciendo falta para todo lo demas —`datos-bot`,
//  las listas—, que no lleva la clinica en la clave.
suscribirVisita(() => { queryClient.clear() })
// (6-oct-2026) Un módulo apagado mientras se trabaja (services/api, 403 `modulo_apagado`): los permisos se vuelven a pedir
// YA, y con ellos el menú y las tarjetas del inicio de administración. Sin esto, hasta un minuto después.
try { window.addEventListener('biango:modulo-apagado', () => { queryClient.invalidateQueries({ queryKey: ['permisos-mias'] }) }) } catch (_) { /* sin ventana */ }
alLimpiarSesion(() => { queryClient.clear() })

// t523 · B-2259 · CON UN IDIOMA RECORDADO en este dispositivo (que no sea el castellano), primero su diccionario y después la
// primera pantalla, ya en ese idioma (como mucho 1,5 s: si tarda, en castellano). Sin nada recordado, se pinta YA, igual que
// siempre (utils/i18nCargar · prepararIdiomaGuardado). Con la función «idiomas» apagada nunca hay nada recordado.
const pintar = () => ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* LA CONFIGURACION REGIONAL ENVUELVE A TODA LA APLICACION, y tiene que estar AQUI dentro del
          QueryClientProvider (lee la suya con react-query) y FUERA del router: la zona horaria, el
          formato de fecha, la moneda y el idioma los usa cualquier pantalla, no solo las de dentro
          de una ruta. Sin este proveedor, `useRegional()` devuelve su valor de emergencia y
          `aplicar()` no hace nada: la pantalla de Configuracion regional guardaria los cambios y no
          se notarian en ninguna parte hasta recargar — que es justo el fallo que este modulo venia
          a evitar. */}
      <RegionalProvider>
        {/* t603 · B-2443 · por el puente de una clínica con la edición local, las pantallas viven debajo de /l/<instalación>. */}
        <BrowserRouter basename={puenteLocal() ? puenteLocal().base : undefined}>
          <App />
        </BrowserRouter>
      </RegionalProvider>
    </QueryClientProvider>
  </React.StrictMode>
)
const delPaciente = esPantallaDelPaciente(window.location.pathname)
if (delPaciente) instalarIdiomaPublico(axios)
const idiomaAntes = delPaciente ? null : prepararIdiomaGuardado()
if (idiomaAntes) idiomaAntes.finally(pintar)
else pintar()
